﻿import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import printJS from "print-js";
import { toast } from "react-toastify";

import { FETCH_USERS_SUCCESS, FINAL_USERS_REQUEST, INITIAL_USERS_REQUEST } from "@/redux/types/maintainers/users.type";
import useProcessingStore from "@/stores/useProcessingStore";

import classes from "../maintainers.module.scss";

import api from "@/services/api.service";
import { hasPermission } from "@/services/Utils";

import { base64toBlob } from "@/hooks/printer/useBase64";

import Head from "@/components/Content/Head";
import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import ButtonFilter from "@/components/Filter/ButtonFilter";
import Detail from "@/components/Detail/Detail";

import UsersTableHead from "./UsersTableHead";
import UsersTableBody from "./UsersTableBody";
import UsersForm from "./UsersForm/UsersForm";
import UsersFilter from "./UsersFilter";

import { filters } from "./users.constants";

const Users = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const startProcessing = useProcessingStore((state) => state.startProcessing);
	const stopProcessing = useProcessingStore((state) => state.stopProcessing);

	const { search, estate_parent_id, estate_id } = useSelector((state) => state.auth.filter);
	const { isFetching, quantity, last_page, from, to, customers } = useSelector((state) => state.maintainers_users);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(20);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("name");
	const [orderType, setOrderType] = useState("asc");
	const [loaded, setLoaded] = useState(false);
	const [filter, setFilter] = useState({ ...filters });
	const [showFilter, setShowFilter] = useState(false);

	const [permissions, setPermissions] = useState([]);

	const [showDelete, setShowDelete] = useState(false);

	const [titleDetail, setTitleDetail] = useState("");
	const [itemsDetail, setItemsDetail] = useState([]);
	const [showDetail, setShowDetail] = useState(false);

	useEffect(() => {
		dispatch({ type: INITIAL_USERS_REQUEST });
		api.get("/admin/users", {
			page,
			paginate,
			order,
			orderType,
			search,
			estate_parent_id,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
			...filter,
		}).then((response) => {
			updateData(response);
		});
		// eslint-disable-next-line
	}, [search, estate_parent_id, estate_id, order, orderType, paginate, page, filter]);

	const updateData = (response) => {
		if (Object.prototype.hasOwnProperty.call(response, "users")) {
			const { users, customers, estates_parents, estates, areas, roles, notifications, base_permissions, permissions, profiles } = response;
			setPermissions(permissions);
			dispatch({
				type: FETCH_USERS_SUCCESS,
				payload: {
					users: users.data,
					last_page: users.last_page,
					from: users.from,
					to: users.to,
					quantity: users.total,
					customers: customers,
					estates_parents: estates_parents,
					estates: estates,
					areas: areas,
					roles: roles,
					notifications: notifications,
					base_permissions: base_permissions,
					profiles: profiles,
				},
			});
			setLoaded(true);
		} else {
			setLoaded(true);
		}
	};

	const handleDelete = (item) => {
		setSelected(item.id);
		setShowDelete(true);
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		dispatch({ type: INITIAL_USERS_REQUEST });
		api.post("/admin/users/delete", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			estate_parent_id,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			setSelected("");
			if (Object.prototype.hasOwnProperty.call(response, "users")) {
				toast.success("Usuario eliminado correctamente.");
				updateData(response);
			} else {
				dispatch({ type: FINAL_USERS_REQUEST });
			}
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/users/save", {
			...form.form,
			page,
			paginate,
			order,
			orderType,
			search,
			estate_parent_id,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			if (Object.prototype.hasOwnProperty.call(response, "users")) {
				updateData(response);
				modalFetching(false);
				afterSave();
			} else {
				modalFetching(false);
			}
		});
	};

	const applyFilter = (params) => {
		let new_filter = { ...filter };

		if (params.filterCustomer !== filter.customer_id) {
			new_filter = { ...new_filter, customer_id: params.filterCustomer };
		}
		if (params.filterEstateParent !== filter.filter_estate_parent_id) {
			new_filter = { ...new_filter, filter_estate_parent_id: params.filterEstateParent };
		}
		if (params.filterEstate !== filter.filter_estate_id) {
			new_filter = { ...new_filter, filter_estate_id: params.filterEstate };
		}
		if (params.filterRole !== filter.role_id) {
			new_filter = { ...new_filter, role_id: params.filterRole };
		}

		setFilter(new_filter);
		setShowFilter(false);
	};

	const printQR = async (user) => {
		startProcessing("Generando Código QR...");

		try {
			const response = await api.post("/admin/users/pdf", {
				user_id: user.id,
			});

			if (response?.url) {
				const blobUrl = URL.createObjectURL(base64toBlob(`data:application/pdf;base64,${response.url}`));

				printJS(blobUrl);
			} else {
				toast.error("No se pudo generar el PDF.");
			}
		} catch {
			toast.error("Hubo un error al generar el PDF.");
		} finally {
			stopProcessing();
		}
	};

	if (isFetching) {
		return <Fetching />;
	}
	return (
		<>
			{" "}
			<Head
				title="Usuarios"
				button={hasPermission("create", permissions) ? true : false}
				button_text={"Nuevo Usuario"}
				action={() =>
					location.pathname.includes("configuracion") ? navigate("/configuracion/usuarios/crear") : navigate("/mantenedores/usuarios/crear")
				}
				back_url={location.pathname.includes("configuracion") ? "/configuracion?section=general" : "/mantenedores"}
			/>
			<ButtonFilter origin="main" size_md={12} showFilter={showFilter} setShowFilter={setShowFilter} />
			{hasPermission("show", permissions) && (
				<Row className={`no_print ${classes.main_row}`}>
					{showFilter && <UsersFilter filter={filter} applyFilter={applyFilter} />}

					<Col md={showFilter ? 9 : 12} className={classes.main_col}>
						<Row className={classes.header}>
							<ButtonFilter origin="actions" size_md={1} showFilter={showFilter} setShowFilter={setShowFilter} />
						</Row>

						<Row className={classes.table}>
							<div className={classes.table_div}>
								<Table className={classes.table_desktop}>
									<UsersTableHead order={order} setOrder={setOrder} orderType={orderType} setOrderType={setOrderType} />
									<UsersTableBody
										handleDelete={handleDelete}
										setTitleDetail={setTitleDetail}
										setItemsDetail={setItemsDetail}
										setShowDetail={setShowDetail}
										printQR={printQR}
										permissions={permissions}
									/>
								</Table>
							</div>
						</Row>

						<BasePagination
							page={page}
							paginate={paginate}
							last_page={last_page}
							quantity={quantity}
							from={from}
							to={to}
							setPage={setPage}
							setPaginate={setPaginate}
						/>
					</Col>
				</Row>
			)}
			<Detail title={titleDetail} items={itemsDetail} isOpen={showDetail} handleClose={setShowDetail} />
			{loaded && (
				<>
					<BasicConfirm showConfirm={showDelete} setShowConfirm={setShowDelete} handleAction={confirmDelete} />
					<UsersForm customers={customers} confirmSave={confirmSave} permissions={permissions} />
				</>
			)}
		</>
	);
};

export default Users;
